import { AfterViewInit, Component, Input, OnDestroy } from '@angular/core';
import * as echarts from 'echarts';
import { Subscription } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';

@Component({
  selector: 'app-pie-chart',
  templateUrl: './pie-chart.component.html',
  styleUrls: ['./pie-chart.component.scss']
})
export class PieChartComponent implements AfterViewInit, OnDestroy {
  @Input() data!: any;
  private myChart!: echarts.ECharts;
  private filterSub?: Subscription;

  constructor(
    private configService: ConfigService,
    private globalFilterService: GlobalFilterService
  ) {}

  ngAfterViewInit(): void {
    this.loadAndRenderChart();

    this.filterSub = this.globalFilterService.filter$.subscribe((filters) => {
      if (!this.shouldHandleFilterChange(filters)) {
        return;
      }

      this.loadAndRenderChart(filters);
    });
  }

  private shouldHandleFilterChange(filters?: any): boolean {
    if (!filters || Object.keys(filters).length === 0) {
      return false;
    }

    const triggerWidgetId = String(filters?.widgetId || '').trim();
    if (!triggerWidgetId || triggerWidgetId === this.data?.widgetId) {
      return false;
    }

    return this.getFilterListeners().includes(triggerWidgetId);
  }

  private getFilterListeners(): string[] {
    const configuredListeners = this.data?.filterListener ?? this.data?.filterListner ?? [];

    if (Array.isArray(configuredListeners)) {
      return configuredListeners
        .map((listener) => String(listener || '').trim())
        .filter((listener) => !!listener);
    }

    const singleListener = String(configuredListeners || '').trim();
    return singleListener ? [singleListener] : [];
  }

  private loadAndRenderChart(filters?: any): void {
    const chartDom = document.getElementById(this.data.widgetId);
    if (!chartDom) return;

    if (!this.myChart) {
      this.myChart = echarts.init(chartDom);
    }

    this.myChart.showLoading();

    this.getChartRequest(filters).subscribe({
      next: (res) => {
        this.renderChart(this.normalizeChartData(res));
        this.addResizeListener();
      },
      error: () => {
        this.myChart.showLoading();
      }
    });
  }

  private getChartRequest(filters?: any) {
    const method = String(this.data?.method || 'post').toLowerCase();

    if (this.data?.payload) {
      return this.configService.getApi(
        this.data.apiUrl,
        {
          queryParams: filters?.queryParams || {},
          rawQueryString: filters?.rawQueryString
        },
        {
          skipDefaultPayload: true,
          customPayload: this.buildRequestPayload(filters),
          useQueryParam: this.data?.useQueryParams === true
        },
        method
      );
    }

    return this.configService.getConfig(
      this.data.apiUrl,
      {
        ...(filters || {}),
        useQueryParams: this.data?.useQueryParams || false
      },
      method
    );
  }

  private buildRequestPayload(filters?: any): any {
    const basePayload = this.deepClone(this.data?.payload || {});
    const requestParams = filters?.requestParams || {};

    if (Object.keys(requestParams).length === 0) {
      return basePayload;
    }

    if (
      basePayload?.requestParams &&
      typeof basePayload.requestParams === 'object' &&
      !Array.isArray(basePayload.requestParams)
    ) {
      return {
        ...basePayload,
        requestParams: {
          ...basePayload.requestParams,
          ...requestParams
        }
      };
    }

    return {
      ...basePayload,
      ...requestParams
    };
  }

  private deepClone<T>(value: T): T {
    return value == null ? value : JSON.parse(JSON.stringify(value));
  }

  private normalizeChartData(response: any): Array<{ name: string; value: number }> {
    const source = this.resolveChartDataSource(response);

    if (!Array.isArray(source)) {
      return [];
    }

    return source
      .map((item, index) => this.normalizeChartPoint(item, index))
      .filter((item): item is { name: string; value: number } => item !== null);
  }

  private resolveChartDataSource(response: any): any[] {
    const dataKey = String(this.data?.dataKey || '').trim();

    if (dataKey) {
      const resolved = this.resolvePath(response, dataKey);
      if (Array.isArray(resolved)) {
        return resolved;
      }
    }

    if (Array.isArray(response?.data)) {
      return response.data;
    }

    if (Array.isArray(response)) {
      return response;
    }

    return [];
  }

  private normalizeChartPoint(item: any, index: number): { name: string; value: number } | null {
    if (item == null) {
      return null;
    }

    if (typeof item !== 'object' || Array.isArray(item)) {
      const primitiveValue = this.toNumber(item);
      return primitiveValue === null
        ? null
        : { name: `Item ${index + 1}`, value: primitiveValue };
    }

    if (item.name !== undefined && item.value !== undefined) {
      const normalizedValue = this.toNumber(item.value);
      if (normalizedValue === null) {
        return null;
      }

      return {
        ...item,
        name: String(item.name),
        value: normalizedValue
      };
    }

    const valueKey = this.resolveValueKey(item);
    const nameKey = this.resolveNameKey(item, valueKey);

    if (!nameKey || !valueKey) {
      return null;
    }

    const normalizedValue = this.toNumber(item[valueKey]);
    if (normalizedValue === null) {
      return null;
    }

    return {
      ...item,
      name: String(item[nameKey] ?? `Item ${index + 1}`),
      value: normalizedValue
    };
  }

  private resolveNameKey(item: Record<string, any>, valueKey: string): string {
    const configuredKeys = [
      this.data?.categoryKey,
      this.data?.nameKey,
      this.data?.labelKey,
      this.data?.series
    ]
      .map((key: any) => String(key || '').trim())
      .filter((key: string) => !!key);

    for (const key of configuredKeys) {
      if (key in item) {
        return key;
      }
    }

    const preferredKeys = ['name', 'label', 'category', 'executive', 'status', 'title'];
    for (const key of preferredKeys) {
      if (key in item) {
        return key;
      }
    }

    const firstTextKey = Object.keys(item).find((key) => {
      if (key === valueKey || this.isPercentKey(key)) {
        return false;
      }

      const value = item[key];
      return typeof value === 'string' && this.toNumber(value) === null;
    });

    if (firstTextKey) {
      return firstTextKey;
    }

    return Object.keys(item).find((key) => key !== valueKey) || '';
  }

  private resolveValueKey(item: Record<string, any>): string {
    const configuredValueKey = String(this.data?.valueKey || '').trim();
    if (configuredValueKey && configuredValueKey in item) {
      return configuredValueKey;
    }

    const preferredKeys = ['value', 'count', 'total', 'amount', 'revenue', 'sales', 'net_sales'];
    for (const key of preferredKeys) {
      if (key in item && this.toNumber(item[key]) !== null) {
        return key;
      }
    }

    return (
      Object.keys(item).find((key) =>
        !this.isPercentKey(key) && this.toNumber(item[key]) !== null
      ) || ''
    );
  }

  private isPercentKey(key: string): boolean {
    const normalizedKey = String(key || '').toLowerCase();
    return normalizedKey.includes('percent') || normalizedKey.endsWith('_pct') || normalizedKey.endsWith('pct');
  }

  private toNumber(value: any): number | null {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    if (typeof value !== 'string') {
      return null;
    }

    const normalizedValue = value.replace(/,/g, '').trim();
    if (!normalizedValue) {
      return null;
    }

    const parsedValue = parseFloat(normalizedValue.replace(/%$/, ''));
    return Number.isFinite(parsedValue) ? parsedValue : null;
  }

  private resolvePath(obj: any, path: string): any {
    try {
      return path.split('.').reduce((acc, part) => acc && acc[part], obj);
    } catch {
      return undefined;
    }
  }

  private renderChart(chartData?: Array<{ name: string; value: number }>): void {
    const option = {
      title: {
        left: 'center'
      },
      tooltip: {
        trigger: 'item',
        formatter: '{a} <br/>{b} : {c} ({d}%)'
      },
      legend: {
        orient: 'vertical',
        left: 'left',
      },
      series: [
        {
          name: this.data.title || '',
          type: 'pie',
          radius: '50%',
          data: chartData,
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          }
        }
      ]
    };

    this.myChart.setOption(option);
    setTimeout(() => {
      this.myChart.hideLoading();
    }, 1000);
  }

  private addResizeListener(): void {
    window.removeEventListener('resize', this.handleResize);
    window.addEventListener('resize', this.handleResize);
  }

  private handleResize = (): void => {
    this.myChart?.resize();
  };

  ngOnDestroy(): void {
    this.filterSub?.unsubscribe();
    window.removeEventListener('resize', this.handleResize);
    this.myChart?.dispose();
  }
}
